import { inject, Injectable } from "@angular/core";
import { CanActivate, Router } from "@angular/router";
import { compact, first } from "lodash";
import {
  catchError,
  defer,
  forkJoin,
  iif,
  lastValueFrom,
  map,
  of,
  switchMap
} from "rxjs";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { UserBackend } from "src/app/backend/api/user.backend";
import { Member, MemberScope } from "src/app/backend/models/member/member";
import { MemberAuth } from "src/app/backend/models/member/member-auth";
import { LICENSES } from "src/app/core/modules";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { ErrorCodes } from "src/app/modules/maintenance/error-codes";
import { maintenance } from "src/app/support/maintenance";

@Injectable()
export class ProvideAdminMemberAuthGuard implements CanActivate {
  private readonly userBackend = inject(UserBackend);
  private readonly memberBackend = inject(MemberBackend);
  private readonly bootstrapService = inject(BootstrapService);
  private readonly router = inject(Router);

  async canActivate(): Promise<boolean> {
    const platformMembers: Array<{
      memberAuth: MemberAuth;
      member: Member;
    }> | null = await lastValueFrom(
      this.userBackend.loadMembersByScope(MemberScope.PLATFORM).pipe(
        switchMap((members: MemberAuth[]) => {
          return iif(
            () => members.length > 0,
            defer(() =>
              forkJoin(
                members.map((memberAuth: MemberAuth) => {
                  return this.memberBackend
                    .loadMeWithToken({
                      memberToken: `Bearer ${memberAuth.token}`
                    })
                    .pipe(
                      map((member: Member) => {
                        return {
                          memberAuth,
                          member
                        };
                      }),
                      catchError(() => of(null))
                    );
                })
              )
            ),
            of(null)
          );
        }),
        map(
          (
            membersOrNull: Array<{
              memberAuth: MemberAuth;
              member: Member;
            } | null> | null
          ) => {
            if (membersOrNull === null) {
              return null;
            }

            return compact(membersOrNull);
          }
        )
      )
    );

    if (!platformMembers) {
      maintenance(this.router, { code: ErrorCodes.ERR_011 });
      return false;
    }

    if (platformMembers.length === 0) {
      maintenance(this.router, { code: ErrorCodes.ERR_012 });
      return false;
    }

    const selectedMember = first(platformMembers)!;

    this.bootstrapService.setCurrentTenant({
      id: "",
      locale: "it-IT",
      name: "Admin",
      number: "",
      plan: {
        id: LICENSES.ADVANCED,
        members: 10000
      },
      type: "PMS",
      region: "IT",
      timezone: "Europe/Rome"
    });

    this.bootstrapService.setCurrentMemberAuth(selectedMember.memberAuth);

    return true;
  }
}
